import React, {useState, useEffect, useRef} from 'react';
import post from '../utils/post.js';
import AppHeader from '../components/AppHeader.js';
import Search from '../components/Search.js';
import CalendarIn from '../components/CalendarIn.js';
import CalendarNew from '../components/CalendarNew.js';
import CalendarEdit from '../components/CalendarEdit.js';

export default function CalendarEvents({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [dataSearch, setDataSearch] = useState([]);
	const [dataDots, setDataDots] = useState({});
	const [searchValue, setSearchValue] = useState('');
	const [showNew, setShowNew] = useState(false);
	const [newDT, setNewDT] = useState('');
	const [today, setToday] = useState(new Date().getDate() + '.' + (new Date().getMonth() + 1) + '.' + new Date().getFullYear());
	const [showEdit, setShowEdit] = useState(false);
	const [eventData, setEventData] = useState({});
	const [sharedUsers, setSharedUsers] = useState([]);
	const calendarEventsRef = useRef(null);

	useEffect(() => {
		getData('', today);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onChangeSearch = (value) => {
		setSearchValue(value);
		getData(value, today);
	};

	const getData = (search, date) => {
		setLoading(true);

		post('getListCalendarEvents', {search: search, date: date})
			.then((res) => {
				setData(res.data);
				setDataSearch(res.dataSearch);
				setDataDots(res.dataDots);
				setSharedUsers(res.dataSharedUsers);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const todayChange = (val) => {
		setToday(val);
		calendarEventsRef.current?.scrollTo({top: 0});
		
		getData('', val);
	};

	const handleNew = (date) => {
		setNewDT(date);
		setShowNew(true);
	};

	const updateSharedUsers = (nextSharedUsers) => {
		setSharedUsers(Array.isArray(nextSharedUsers) ? nextSharedUsers : []);
	};

	const updateCalendarRowNew = () => {
		getData(searchValue, today);
	}

	const updateCalendarRowEdit = () => {
		getData(searchValue, today);
	}

	const isEditableEvent = (event) => {
		return parseInt(event.USRID) === parseInt(usrInfo.usrId);
	};

	const openEventEditor = (event) => {
		if (!isEditableEvent(event)) {
			return;
		}

		setEventData(event);
		setShowEdit(true);
	};

	const getEventTimeText = (event) => {
		const parts = [];

		if (event.TIMEEVENT !== '00:00') {
			parts.push(event.TIMEEVENT);
		}

		if (event.TIMEEVENTEND !== '23:59' && event.TIMEEVENTEND !== '00:00') {
			parts.push(event.TIMEEVENTEND);
		}

		return parts.join(' - ');
	};

	const getEventTone = (repeatId) => {
		const parsedRepeatId = parseInt(repeatId, 10);

		if (Number.isNaN(parsedRepeatId)) {
			return 0;
		}

		return Math.min(Math.max(parsedRepeatId, 0), 3);
	};

	const renderEventCard = (event, options = {}) => {
		const {key, showDate = false} = options;
		const isEditable = isEditableEvent(event);
		const timeText = getEventTimeText(event);
		const tone = getEventTone(event.REPEATID);

		return (
			<div
				key={key || `event_${event.ID}`}
				className={`calendar-events-page__event calendar-events-page__event--tone-${tone}${isEditable ? ' is-editable' : ' is-readonly'}`}
				onClick={isEditable ? () => openEventEditor(event) : undefined}
				onKeyDown={isEditable ? (eventKeyDown) => {
					if (eventKeyDown.key === 'Enter' || eventKeyDown.key === ' ') {
						eventKeyDown.preventDefault();
						openEventEditor(event);
					}
				} : undefined}
				role={isEditable ? 'button' : undefined}
				tabIndex={isEditable ? 0 : undefined}
			>
				<div className="calendar-events-page__event-marker"></div>
				<div className="calendar-events-page__event-content">
					<div className="calendar-events-page__event-head">
						{showDate ? <span className="calendar-events-page__event-date">{event.STARTDTS}</span> : null}
						{timeText !== '' ? <span className="calendar-events-page__event-time">{timeText}</span> : null}
					</div>
					<div className="calendar-events-page__event-name">{event.NAME}</div>
				</div>
				{event.SHAREDUSERS_CNT > 0 && isEditable ? <img className="icon_shared calendar-events-page__event-share" alt="" /> : null}
			</div>
		);
	};

	const weekSections = [];
	let currentWeekSection = null;

	data.forEach((item, index) => {
		if (item.dayOfWeek === 1 || currentWeekSection === null) {
			currentWeekSection = {
				key: `week_${item.weekNum}_${index}`,
				weekNum: item.weekNum,
				days: []
			};

			weekSections.push(currentWeekSection);
		}

		currentWeekSection.days.push(item);
	});

	const list = weekSections.map((section) => {
		return (
			<section className="calendar-events-page__week" key={section.key}>
				<div className="calendar-events-page__week-header">
					<span className="calendar-events-page__week-label">{section.weekNum}. týden</span>
					<span className="calendar-events-page__week-count">{section.days.length} dní</span>
				</div>
				<div className="calendar-events-page__days">
					{section.days.map((item) => (
						<article className={`calendar-events-page__day${item.events?.length > 0 ? '' : ' is-empty'}`} key={`item_${item.dt}`}>
							<div className="calendar-events-page__day-header">
								<button type="button" className="calendar-events-page__day-add" onClick={() => handleNew(item.dt)} aria-label={`Přidat událost pro ${item.days} (${item.dayName})`}>
									+
								</button>
								<div className="calendar-events-page__day-text">
									<div className="calendar-events-page__day-title">{item.days} ({item.dayName})</div>
									<div className="calendar-events-page__day-subtitle">{item.dayNames}</div>
								</div>
							</div>
							{item.events?.length > 0 ? (
								<div className="calendar-events-page__event-list">
									{item.events.map((event) => renderEventCard(event, {key: `event_${event.ID}`}))}
								</div>
							) : null}
						</article>
					))}
				</div>
			</section>
		);
	});

	const listSearch = dataSearch.map((item) => {
		return renderEventCard(item, {key: `event_${item.ID}`, showDate: true});
	});

	return (
		<>
			<div className="page_main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
				<Search showSearch searchValue={searchValue} onChangeSearch={(e) => onChangeSearch(e)} />
				<CalendarNew showNew={showNew} closeNew={() => setShowNew(false)} newDT={newDT} updateCalendarRowNew={updateCalendarRowNew} sharedUsers={sharedUsers} onSharedUsersChange={updateSharedUsers} usrInfo={usrInfo} />
				<CalendarEdit showEdit={showEdit} closeEdit={() => setShowEdit(false)} eventData={eventData} updateCalendarRowEdit={updateCalendarRowEdit} sharedUsers={sharedUsers} onSharedUsersChange={updateSharedUsers} usrInfo={usrInfo} />

				<div className={`page calendar-events-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
					<CalendarIn defaultValue={today} returnValue={todayChange} dataEvents={dataDots} />
					<div className="zero h05" />

					<div className="calendar_events" ref={calendarEventsRef}>
						{list.length > 0 ? <div className="calendar-events-page__weeks">{list}</div> : null}
						{listSearch.length > 0 ? <div className="calendar-events-page__search-list">{listSearch}</div> : null}
						{list.length === 0 && listSearch.length === 0 ? <div className="calendar-events-page__empty">Žádné události nebyly nalezeny.</div> : null}
					</div>
				</div>
			</div>
		</>
	);
}
